import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import communityAPI from '../../../api/community';
import { message } from 'antd';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import Card from '../../../componentes/ui/Card';
import Avatar from '../../../componentes/ui/Avatar';
import { InlineSpinner } from '../../../componentes/LoadingIndicator';
import { Eye, EyeOff, AlertTriangle, Trash2, ShieldCheck } from 'lucide-react';
import ConfirmModal from '../shared/ConfirmModal';

const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/pjpeg', 'image/png', 'image/x-png', 'image/webp', 'image/gif'];
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Using shared axios instance configured with correct host:port

export default function EditProfile() {
  // Normalize media URLs to include host:port from VITE_APP_API_URL
  const fixMediaUrl = (url) => {
    try {
      if (typeof url !== 'string' || !url) return url;
      if (!url.includes('/media/')) return url;
      const apiUrl = import.meta.env.VITE_APP_API_URL;
      const { protocol, host } = new URL(apiUrl);
      // If url is absolute, swap host; if relative, prefix with protocol//host
      if (/^https?:\/\//i.test(url)) {
        return url.replace(/(https?:\/\/)[^/]+(\/media\/)/i, `${protocol}//${host}$2`);
      }
      return `${protocol}//${host}${url.startsWith('/') ? '' : '/'}${url}`;
    } catch {
      return url;
    }
  };
  const navigate = useNavigate();
  const { user, login, logout } = useCommunityAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    phone: '',
    location: '',
    bio: '',
    skills: '',
    avatar: '',
    cover_image: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [coverPreview, setCoverPreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState({ avatar: false, cover: false });
  const [isSaving, setIsSaving] = useState(false);
  const [deleteMode, setDeleteMode] = useState('purge');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const initialMediaRef = useRef({ avatar: '', cover_image: '' });

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        title: user.title || '',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || '',
        skills: Array.isArray(user.skills) ? user.skills.join(', ') : '',
        avatar: user.avatar || user.avatar_url || '',
        cover_image: user.cover_image || user.cover_url || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

      const nextAvatar = fixMediaUrl(user.avatar || user.avatar_url || '');
      const nextCover = fixMediaUrl(user.cover_image || user.cover_url || '');
      setAvatarPreview(nextAvatar);
      setCoverPreview(nextCover);
      initialMediaRef.current = {
        avatar: user.avatar || user.avatar_url || '',
        cover_image: user.cover_image || user.cover_url || ''
      };

      setDeleteMode('purge');
      setDeletePassword('');
      setDeleteConfirmation('');
      setShowDeleteModal(false);
      setIsDeleting(false);
    }
  }, [user]);

  const syncStateFromResponse = (payload = {}, { preserveEmail = false } = {}) => {
    const nextAvatar = payload.avatar || payload.avatar_url || formData.avatar || '';
    const nextCover = payload.cover_image || payload.cover_url || formData.cover_image || '';
    const nextSkills = Array.isArray(payload.skills)
      ? payload.skills.join(', ')
      : formData.skills;

    setFormData((prev) => ({
      ...prev,
      username: payload.username ?? prev.username,
      email: preserveEmail ? prev.email : (payload.email ?? prev.email),
      first_name: payload.first_name ?? prev.first_name,
      last_name: payload.last_name ?? prev.last_name,
      title: payload.title ?? prev.title,
      phone: payload.phone ?? prev.phone,
      location: payload.location ?? prev.location,
      bio: payload.bio ?? prev.bio,
      avatar: nextAvatar,
      cover_image: nextCover,
      skills: nextSkills,
      current_password: '',
      new_password: '',
      confirm_password: ''
    }));

    setAvatarPreview(fixMediaUrl(nextAvatar));
    setCoverPreview(fixMediaUrl(nextCover));
    initialMediaRef.current = { avatar: nextAvatar, cover_image: nextCover };
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const uploadMediaFile = async (file, mediaType) => {
    const response = await communityAPI.users.uploadMedia(file, mediaType);
    const data = response.data || {};
    return data.url || data.avatar_url || data.cover_url || null;
  };

  const handleImageChange = async (event, type) => {
    const input = event.target;
    const file = input.files && input.files[0];
    if (!file) return;

    const extension = (file.name || '').split('.').pop()?.toLowerCase() || '';
    const mimeAllowed = file.type ? ALLOWED_IMAGE_MIME_TYPES.includes(file.type) : false;
    const extensionAllowed = extension ? ALLOWED_IMAGE_EXTENSIONS.includes(extension) : false;

    if (!mimeAllowed && !extensionAllowed) {
      message.error('Please choose a JPG, PNG, GIF, or WebP image.');
      input.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      message.error('Images must be 5MB or smaller.');
      input.value = '';
      return;
    }

    const fallbackUrl = type === 'avatar'
      ? formData.avatar || ''
      : formData.cover_image || '';

    const tempUrl = URL.createObjectURL(file);
    if (type === 'avatar') {
      setAvatarPreview(tempUrl);
    } else {
      setCoverPreview(tempUrl);
    }

    try {
      setUploadingImage((prev) => ({ ...prev, [type]: true }));
      const uploadedUrl = await uploadMediaFile(file, type);
      if (!uploadedUrl) {
        throw new Error('Upload failed');
      }

      setFormData((prev) => ({
        ...prev,
        [type === 'avatar' ? 'avatar' : 'cover_image']: uploadedUrl
      }));

      const normalized = fixMediaUrl(uploadedUrl);
      if (type === 'avatar') {
        setAvatarPreview(normalized);
      } else {
        setCoverPreview(normalized);
      }

      message.success(`${type === 'avatar' ? 'Avatar' : 'Cover image'} uploaded successfully`);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || 'Image upload failed');
      if (type === 'avatar') {
        setAvatarPreview(fallbackUrl);
      } else {
        setCoverPreview(fallbackUrl);
      }
    } finally {
      setUploadingImage((prev) => ({ ...prev, [type]: false }));
      URL.revokeObjectURL(tempUrl);
      input.value = '';
    }
  };

  const handleImageRemove = (type) => {
    if (type === 'avatar') {
      setFormData((prev) => ({ ...prev, avatar: '' }));
      setAvatarPreview('');
      message.success('Avatar removed. Save changes to apply.');
      return;
    }

    setFormData((prev) => ({ ...prev, cover_image: '' }));
    setCoverPreview('');
    message.success('Cover image removed. Save changes to apply.');
  };

  const submitProfileUpdate = async ({ silent = false, suppressError = false } = {}) => {
    try {
      setIsSaving(true);

      const desiredEmail = (formData.email || '').trim().toLowerCase();
      const currentEmail = (user?.email || '').trim().toLowerCase();
      const isEmailChangeRequested = Boolean(desiredEmail && desiredEmail !== currentEmail);

      const skillsList = formData.skills
        .split(',')
        .map((skill) => skill.trim())
        .filter(Boolean);

      const updateData = {
        username: formData.username,
        first_name: formData.first_name,
        last_name: formData.last_name,
        title: formData.title,
        phone: formData.phone,
        location: formData.location,
        bio: formData.bio,
        cover_image: formData.cover_image,
        avatar: formData.avatar,
        skills: skillsList
      };

      if (!isEmailChangeRequested) {
        updateData.email = formData.email;
      }

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      const res = await communityAPI.users.partialUpdate(user.username, updateData);

      if (!silent && !isEmailChangeRequested) {
        message.success('Profile updated successfully!');
      }

      login(res.data);

      syncStateFromResponse(res.data, { preserveEmail: isEmailChangeRequested });
      return true;
    } catch (err) {
      if (!suppressError) {
        message.error(err.response?.data?.error || 'Error updating profile');
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    if (formData.new_password && formData.new_password !== formData.confirm_password) {
      message.error('Passwords do not match');
      return;
    }

    const desiredEmail = (formData.email || '').trim().toLowerCase();
    const currentEmail = (user?.email || '').trim().toLowerCase();
    const emailChanged = Boolean(desiredEmail && desiredEmail !== currentEmail);

    const saved = await submitProfileUpdate();
    if (!saved) return;

    if (emailChanged) {
      try {
        await communityAPI.users.requestEmailChange(user.username, desiredEmail);
        sessionStorage.setItem(
          'community:emailChange',
          JSON.stringify({ username: user.username, newEmail: desiredEmail })
        );
        message.success('Profile saved. Verification code sent to your new email. Verify to apply the email change.');
        navigate('/community/verify-email-change', {
          state: { username: user.username, newEmail: desiredEmail, from: '/community/profile/edit' }
        });
      } catch (err) {
        const apiMessage = err.response?.data?.new_email || err.response?.data?.detail || err.response?.data?.email;
        message.error(apiMessage || 'Profile saved, but unable to start email verification. Please try again.');
      }
    }
  };

  const handleNavigateBack = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    const desiredEmail = (formData.email || '').trim().toLowerCase();
    const currentEmail = (user?.email || '').trim().toLowerCase();
    const emailChanged = Boolean(desiredEmail && desiredEmail !== currentEmail);

    if (
      formData.new_password &&
      formData.new_password !== formData.confirm_password
    ) {
      message.error('Please resolve password mismatch before leaving.');
      return;
    }

    const hasMediaChanges =
      formData.avatar !== initialMediaRef.current.avatar ||
      formData.cover_image !== initialMediaRef.current.cover_image;

    if (emailChanged) {
      message.error('Please save changes to start email verification before leaving.');
      return;
    }

    if (!hasMediaChanges) {
      navigate('/community/profile');
      return;
    }

    const saved = await submitProfileUpdate({ silent: true, suppressError: true });
    if (saved) {
      message.success('Profile changes saved automatically.');
      navigate('/community/profile');
    } else {
      message.error('Could not save changes automatically. Please try again.');
    }
  };

  const expectedUsername = (user?.username || '').toLowerCase();
  const normalizedDeleteConfirmation = deleteConfirmation.trim().toLowerCase();
  const requiresPasswordForDelete = user && user.has_usable_password !== false;
  const canDeleteAccount = Boolean(
    user &&
    normalizedDeleteConfirmation &&
    normalizedDeleteConfirmation === expectedUsername &&
    (!requiresPasswordForDelete || deletePassword.trim())
  );

  const deleteConfirmTitle = deleteMode === 'purge'
    ? 'Remove Account & Contributions'
    : 'Remove Account';
  const deleteConfirmMessage = deleteMode === 'purge'
    ? 'This will permanently remove your account, personal data, and every contribution you have created. This action cannot be undone.'
    : 'This will remove your account and personal data, while approved posts, challenges, and learning paths remain publicly visible under a Former Member profile.';

  const openDeleteModal = () => {
    if (!canDeleteAccount || isDeleting) {
      return;
    }
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) {
      return;
    }
    setShowDeleteModal(false);
  };

  const handleDeleteAccount = async () => {
    if (!user) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await communityAPI.users.deleteAccount(user.username, {
        mode: deleteMode,
        current_password: deletePassword,
        confirm_username: deleteConfirmation,
      });

      const successMessage = response.data?.detail || 'Account deleted successfully.';
      message.success(successMessage);
      logout();
      setDeletePassword('');
      setDeleteConfirmation('');
      setDeleteMode('purge');
      navigate('/community/login');
    } catch (err) {
      const errorData = err.response?.data || {};
      const errorMessage =
        errorData.current_password ||
        errorData.confirm_username ||
        errorData.mode ||
        errorData.detail ||
        'Unable to delete account right now.';
      message.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full text-center p-10 shadow-lg border-0">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-3xl font-bold text-red-600 dark:text-red-400 mb-4">Authentication Required</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-6">You need to be logged in to edit your profile</p>
          <button onClick={() => navigate('/community/login')} className="px-8 py-3 bg-primary hover:bg-[#229bb8] rounded-xl text-white font-semibold shadow-lg transition-all">
            Login Now
          </button>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8 flex items-center gap-4">
            <Link
              to="/community/profile"
              onClick={handleNavigateBack}
              className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Edit Profile</h1>
              <p className="text-gray-600 dark:text-gray-400">Update your profile information and settings</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Cover Photo</h2>
              <div className="relative">
                <div className="relative h-48 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" onError={() => setCoverPreview('')} />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-[#229bb8] via-primary to-primary" />
                  )}
                  <div
                    className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-200 ${uploadingImage.cover ? 'opacity-100' : 'opacity-0 hover:opacity-100'
                      }`}
                  >
                    {uploadingImage.cover ? (
                      <span className="text-white font-semibold">Uploading...</span>
                    ) : (
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <label className="cursor-pointer px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200">
                          Change Cover
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageChange(e, 'cover')}
                            disabled={uploadingImage.cover}
                          />
                        </label>
                        {(formData.cover_image || coverPreview) && (
                          <button
                            type="button"
                            onClick={() => handleImageRemove('cover')}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={uploadingImage.cover}
                          >
                            Remove Cover
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Upload a wide photo (recommended 1600x400px). JPG, PNG, GIF, or WebP up to 5MB.</p>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Profile Information</h2>
              <div className="flex items-center gap-6 mb-6">
                <div className="flex flex-col items-center gap-3">
                  <div className="relative">
                    <Avatar
                      src={avatarPreview}
                      name={formData.username}
                      size={24}
                      className="border-4 border-gray-100 dark:border-gray-600 shadow-lg"
                      isTrusted={user?.is_trusted || user?.status?.is_trusted}
                    />
                    <label
                      className={`absolute bottom-0 right-0 p-2 text-white rounded-full cursor-pointer transition-colors duration-200 ${uploadingImage.avatar
                          ? 'bg-indigo-300 cursor-not-allowed'
                          : 'bg-primary hover:bg-[#229bb8]'
                        }`}
                    >
                      {uploadingImage.avatar ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageChange(e, 'avatar')}
                        disabled={uploadingImage.avatar}
                      />
                    </label>
                  </div>
                  {(formData.avatar || avatarPreview) && (
                    <button
                      type="button"
                      onClick={() => handleImageRemove('avatar')}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded-full text-sm font-semibold hover:bg-red-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={uploadingImage.avatar}
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Profile Photo</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Click the camera icon to update your photo</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={handleFieldChange('first_name')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="Your first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={handleFieldChange('last_name')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="Your last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={handleFieldChange('username')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleFieldChange('email')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleFieldChange('title')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="e.g., Senior Security Analyst"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={handleFieldChange('phone')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="e.g., +1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={handleFieldChange('location')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="City, Country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Skills</label>
                  <textarea
                    value={formData.skills}
                    onChange={handleFieldChange('skills')}
                    rows="2"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
                    placeholder="Comma-separated list, e.g., Threat Hunting, Cloud Security"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">We use commas to split this list into dashboard badges.</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                  <textarea
                    value={formData.bio}
                    onChange={handleFieldChange('bio')}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
                    placeholder="Tell us about yourself..."
                  />
                </div>
              </div>

            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Change Password</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Leave blank to keep current password</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {['current_password', 'new_password', 'confirm_password'].map((field) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {field === 'current_password' && 'Current Password'}
                      {field === 'new_password' && 'New Password'}
                      {field === 'confirm_password' && 'Confirm New Password'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords[field.split('_')[0]] ? 'text' : 'password'}
                        value={formData[field]}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white pr-12"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords((prev) => ({ ...prev, [field.split('_')[0]]: !prev[field.split('_')[0]] }))}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary/80"
                      >
                        {showPasswords[field.split('_')[0]] ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6 border border-red-200/70 bg-white dark:bg-gray-900/40">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="p-3 rounded-xl bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300">
                    <AlertTriangle size={24} />
                  </span>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Delete Account</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Choose how you want your contributions handled. You will be signed out immediately after deletion.
                    </p>
                  </div>
                </div>
                {deleteMode === 'preserve' ? (
                  <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded-full">
                    Contributions retained
                  </span>
                ) : (
                  <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wide bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full">
                    Everything removed
                  </span>
                )}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <label
                  className={`block rounded-xl border px-4 py-4 transition-all duration-200 bg-white/70 dark:bg-gray-900/60 ${deleteMode === 'purge'
                      ? 'border-red-400 shadow-sm shadow-red-100 dark:border-red-500/70'
                      : 'border-gray-200 dark:border-gray-700'
                    }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-lg bg-red-50 text-red-600 dark:bg-red-900/40 dark:text-red-200">
                        <Trash2 size={18} />
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">Delete Everything</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Remove your account and erase every contribution.</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="account-delete-mode"
                      value="purge"
                      checked={deleteMode === 'purge'}
                      onChange={() => setDeleteMode('purge')}
                      className="h-4 w-4 text-red-600 focus:ring-red-500"
                    />
                  </div>
                </label>

                <label
                  className={`block rounded-xl border px-4 py-4 transition-all duration-200 bg-white/70 dark:bg-gray-900/60 ${deleteMode === 'preserve'
                      ? 'border-emerald-400 shadow-sm shadow-emerald-100 dark:border-emerald-500/70'
                      : 'border-gray-200 dark:border-gray-700'
                    }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="p-2 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-200">
                        <ShieldCheck size={18} />
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">Delete Account Only</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Remove personal data while keeping contributions public.</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="account-delete-mode"
                      value="preserve"
                      checked={deleteMode === 'preserve'}
                      onChange={() => setDeleteMode('preserve')}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                  </div>
                </label>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type your username to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(event) => setDeleteConfirmation(event.target.value)}
                    placeholder={user?.username || 'username'}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <span>Current password</span>
                    {!deletePassword && canDeleteAccount === false && deleteConfirmation && (
                      <span className="text-xs text-red-500">Required for verification</span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(event) => setDeletePassword(event.target.value)}
                    placeholder="Enter current password"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl">
                  {deleteMode === 'purge'
                    ? 'Your profile, posts, comments, challenges, and learning paths will be permanently deleted.'
                    : 'Your profile and personal data will be removed. Contributions will display under a Former Member profile so the community retains context.'}
                </p>
                <button
                  type="button"
                  onClick={openDeleteModal}
                  disabled={!canDeleteAccount || isDeleting}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Trash2 size={18} />
                  {isDeleting ? 'Processing...' : 'Delete Account'}
                </button>
              </div>
            </Card>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/community/profile')}
                disabled={isSaving}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-[#229bb8] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <InlineSpinner
                    tone="slate"
                    message="Saving changes..."
                    messageClassName="text-white"
                  />
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteAccount}
        title={deleteConfirmTitle}
        message={deleteConfirmMessage}
        confirmLabel={deleteMode === 'purge' ? 'Delete Everything' : 'Delete Account'}
        isProcessing={isDeleting}
      />
    </>
  );
}
